export type TipoDocumento = 'fatura' | 'conta_pagar' | 'nota_credito' | 'reembolso';
export type Sentido = 'receber' | 'pagar';
export type EstadoDocumento = 'em_aberto' | 'parcial' | 'pago' | 'cancelado';

export interface DocumentoResumo {
  id: string;
  numero: string;
  tipo: TipoDocumento;
  sentido: Sentido;
  descricao: string;
  parceiro_id: string;
  parceiro: string | null;
  emissao: string;
  vencimento: string;
  moeda: string;
  total_centavos: string;
  pago_centavos: string;
  saldo_centavos: string;
  estado: EstadoDocumento;
  atrasado: boolean;
  dias_atraso: number;
  recorrencia_id: string | null;
  empresa_id: string | null;
}

export interface Baixa {
  id: string;
  data: string;
  valor_centavos: string;
  juros_centavos: string;
  multa_centavos: string;
  desconto_centavos: string;
  conta_id: string;
  lancamento_id: string;
  origem: 'manual' | 'tool' | 'pagamentos';
  externo_id: string | null;
  estornada: boolean;
  motivo_estorno: string | null;
}

export interface CobrancaExterna {
  id: string;
  documento_id?: string;
  numero?: string;
  parceiro?: string | null;
  forma: 'pix' | 'boleto';
  estado: 'pendente' | 'criada' | 'erro' | 'paga';
  valor_centavos: string;
  vencimento: string;
  externo_id: string | null;
  link: string | null;
  linha_digitavel: string | null;
  pix_copia_cola: string | null;
  erro: string | null;
  created_at: string;
}

export interface Envio {
  id: string;
  documento_id?: string;
  numero?: string;
  parceiro?: string | null;
  dias: number;
  canal: 'email' | 'whatsapp';
  /** SPEC1-R8-01: pelo resultado do job (enviado ou falhou com motivo), não só pela entrada na fila. */
  estado: 'enfileirado' | 'enviado' | 'falhou' | 'nao_enviado';
  motivo: string | null;
  created_at: string;
}

export interface FichaDocumento extends DocumentoResumo {
  condicao_pagamento: string | null;
  valor_centavos: string;
  juros_centavos: string;
  multa_centavos: string;
  desconto_centavos: string;
  conta_id: string | null;
  periodo: string | null;
  motivo_cancelamento: string | null;
  /** N8: campos personalizados do workspace (contas.documento). */
  extras?: Record<string, unknown> | null;
  itens: { id: string; descricao: string; quantidade: number; valor_unitario_centavos: string; total_centavos: string }[];
  baixas: Baixa[];
  cobrancas: CobrancaExterna[];
  envios: Envio[];
}

export interface Recorrencia {
  id: string;
  tipo: 'fatura' | 'conta_pagar';
  parceiro_id: string;
  parceiro: string | null;
  descricao: string;
  valor_centavos: string;
  moeda: string;
  frequencia: 'mensal' | 'trimestral' | 'anual';
  dia_vencimento: number;
  inicio: string;
  fim: string | null;
  ativa: boolean;
  gerados: number;
  ultimo_periodo: string | null;
  /** DM-01: empresa do grupo dona da recorrência; null = do grupo. */
  empresa_id: string | null;
}

export interface Etapa {
  id?: string;
  dias: number;
  email: boolean;
  whatsapp: boolean;
  template_whatsapp: string | null;
}

export interface ConfigContas {
  multa_bp: number;
  juros_mes_bp: number;
  conta_padrao_id: string | null;
  pagamentos_conectado: boolean;
  pagamentos_provedor: string | null;
  whatsapp_conectado: boolean;
}

export interface ContaFinanceiro {
  id: string;
  nome: string;
  tipo: string;
  moeda: string;
  /** M12.4: empresa da conta (nula = conta do grupo); o documento e a recorrência sem empresa herdam a dela. */
  empresa_id?: string | null;
}

/** Centavos (texto) para moeda local sem float: o Intl recebe o decimal exato em texto. */
export function formatarDinheiro(centavos: string | null | undefined, moeda = 'BRL', idioma = 'pt-BR'): string {
  if (centavos === null || centavos === undefined || centavos === '') return '';
  const c = BigInt(centavos);
  const neg = c < 0n;
  const a = neg ? -c : c;
  const decimal = `${neg ? '-' : ''}${(a / 100n).toString()}.${(a % 100n).toString().padStart(2, '0')}`;
  return new Intl.NumberFormat(idioma, { style: 'currency', currency: moeda }).format(decimal as unknown as number);
}

/** Texto em reais ('1.234,56') para centavos inteiros em texto, sem float. 'invalido' se não reconhece. */
export function reaisParaCentavos(texto: string): string | null {
  const t = texto.trim().replace(/\s|R\$/g, '');
  if (!t) return null;
  const m = /^(\d{1,3}(?:\.\d{3})*|\d+)(?:,(\d{1,2}))?$/.exec(t);
  if (!m) return 'invalido';
  return (BigInt(m[1]!.replace(/\./g, '')) * 100n + BigInt((m[2] ?? '').padEnd(2, '0') || '0')).toString();
}

/** Centavos para o campo de digitação em reais ('154990' vira '1549,90'). */
export function centavosParaCampo(c: string): string {
  const v = BigInt(c);
  return `${(v / 100n).toString()},${(v % 100n).toString().padStart(2, '0')}`;
}

export const formatarData = (iso: string | null | undefined, idioma: string) => (iso ? new Intl.DateTimeFormat(idioma, { timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`)) : '');
/** VA7-03: período de recorrência (AAAA-MM) em mês e ano do idioma ativo ("outubro de 2026"); fora do formato volta como veio. */
export const formatarPeriodo = (periodo: string | null | undefined, idioma: string) =>
  !periodo ? '' : /^\d{4}-\d{2}$/.test(periodo) ? new Intl.DateTimeFormat(idioma, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${periodo}-01T00:00:00Z`)) : periodo;

/** ROB-R3-02: a API só aceita datas com ano entre 2000 e 2100 (emissão, vencimento, baixa, recorrência). */
export const DATA_MINIMA = '2000-01-01';
export const DATA_MAXIMA = '2100-12-31';
/** Data preenchida fora de 2000 a 2100 (ou incompleta); vazio não é erro aqui, o obrigatório cuida disso. */
export const dataForaDoIntervalo = (d: string) => !!d && (!/^\d{4}-\d{2}-\d{2}$/.test(d) || d < DATA_MINIMA || d > DATA_MAXIMA);

export const hojeIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const somarDiasIso = (iso: string, n: number) => new Date(Date.parse(`${iso}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

export const novaChave = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
